import React, { useMemo, useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import {
  ChevronUp, ChevronDown, ChevronsUpDown, Search, Phone, Mail, MessageSquare, MessageCircle,
  CheckSquare, Square, X, Flame, Thermometer, Snowflake, Filter
} from 'lucide-react';
import { cn } from '../lib/utils';
import { STATUS_ORDER } from './LeadPipelineView';

const PRIORITY_ICON = { Hot: Flame, Warm: Thermometer, Cold: Snowflake };
const PRIORITY_COLOR = {
  Hot: 'text-rose-500 dark:text-rose-400',
  Warm: 'text-amber-500 dark:text-amber-400',
  Cold: 'text-blue-500 dark:text-blue-400',
};

const STATUS_LABEL = STATUS_ORDER.reduce((acc, s) => { acc[s.key] = s.label; return acc; }, {});

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString();
  } catch (e) { return '—'; }
};

const SortHeader = ({ label, sortKey, currentKey, currentDir, onChange, className }) => {
  const active = currentKey === sortKey;
  const Icon = active ? (currentDir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <button
      type="button"
      onClick={() => onChange(sortKey)}
      className={cn(
        'flex items-center gap-1 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors',
        active && 'text-emerald-600 dark:text-emerald-400 font-semibold',
        className
      )}
    >
      {label}
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
    </button>
  );
};

/**
 * NurtureTableView
 * Sortable, filterable list view of leads with inline status / priority dropdowns
 * and contact pills (call/sms/wa/email). Supports multi-select + bulk actions.
 */
export default function NurtureTableView({ leads = [], onSelectLead, onUpdateLead, onBulkUpdateStatus, onBulkDelete }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortKey, setSortKey] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const filtered = useMemo(() => {
    let out = [...leads];
    if (statusFilter !== 'all') out = out.filter(l => l.status === statusFilter);
    if (priorityFilter !== 'all') out = out.filter(l => l.priority === priorityFilter);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      out = out.filter(l =>
        l.name?.toLowerCase().includes(s) ||
        l.email?.toLowerCase().includes(s) ||
        l.phone?.toLowerCase().includes(s) ||
        l.lead_source?.toLowerCase().includes(s) ||
        l.lead_number?.toLowerCase().includes(s)
      );
    }
    out.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  }, [leads, search, statusFilter, priorityFilter, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(l => l.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectionMode = selectedIds.size > 0;

  // WhatsApp helper
  const waHref = (phone) => {
    if (!phone) return null;
    let digits = phone.replace(/[^\d+]/g, '').replace(/^\+/, '');
    if (digits.startsWith('0')) digits = `61${digits.slice(1)}`;
    return digits ? `https://wa.me/${digits}` : null;
  };

  const handleStatusChange = (lead, newStatus) => {
    if (lead.status === newStatus) return;
    if (onUpdateLead) onUpdateLead(lead.id, { status: newStatus });
  };
  const handlePriorityChange = (lead, newPriority) => {
    if (lead.priority === newPriority) return;
    if (onUpdateLead) onUpdateLead(lead.id, { priority: newPriority });
  };

  return (
    <div className="space-y-3" data-testid="nurture-table-view">
      {/* Filter / search bar */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, email, phone, source…"
            className="pl-9 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
            data-testid="table-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px] dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="table-status-filter">
            <Filter className="w-4 h-4 mr-1" />
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_ORDER.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-full sm:w-[140px] dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="table-priority-filter">
            <SelectValue placeholder="All priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="Hot">Hot</SelectItem>
            <SelectItem value="Warm">Warm</SelectItem>
            <SelectItem value="Cold">Cold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk action bar */}
      {selectionMode && (
        <div
          className="sticky top-0 z-20 flex flex-wrap items-center gap-2 p-3 rounded-xl bg-emerald-600 text-white shadow-lg dark:bg-emerald-700"
          data-testid="table-bulk-bar"
        >
          <span className="font-semibold text-sm">{selectedIds.size} selected</span>
          <select
            className="ml-auto sm:ml-0 px-2 py-1 rounded text-xs text-slate-900 dark:text-slate-100 dark:bg-slate-800"
            defaultValue=""
            data-testid="table-bulk-status"
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              if (onBulkUpdateStatus) onBulkUpdateStatus([...selectedIds], v);
              clearSelection();
            }}
          >
            <option value="">Move to status…</option>
            {STATUS_ORDER.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              if (window.confirm(`Delete ${selectedIds.size} lead(s)? This cannot be undone.`)) {
                if (onBulkDelete) onBulkDelete([...selectedIds]);
                clearSelection();
              }
            }}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="table-bulk-delete"
          >
            Delete
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={clearSelection} className="text-white hover:bg-white/10">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">
            <tr>
              <th className="py-2.5 px-3 w-10">
                <button onClick={toggleSelectAll} aria-label="Select all" data-testid="table-select-all">
                  {selectedIds.size === filtered.length && filtered.length > 0
                    ? <CheckSquare className="w-4 h-4 text-emerald-500" />
                    : <Square className="w-4 h-4 text-slate-400" />}
                </button>
              </th>
              <th className="py-2.5 px-3 text-left">
                <SortHeader label="Name" sortKey="name" currentKey={sortKey} currentDir={sortDir} onChange={toggleSort} />
              </th>
              <th className="py-2.5 px-3 text-left">Contact</th>
              <th className="py-2.5 px-3 text-left">
                <SortHeader label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onChange={toggleSort} />
              </th>
              <th className="py-2.5 px-3 text-left">
                <SortHeader label="Priority" sortKey="priority" currentKey={sortKey} currentDir={sortDir} onChange={toggleSort} />
              </th>
              <th className="py-2.5 px-3 text-left hidden md:table-cell">
                <SortHeader label="Source" sortKey="lead_source" currentKey={sortKey} currentDir={sortDir} onChange={toggleSort} />
              </th>
              <th className="py-2.5 px-3 text-left hidden lg:table-cell">
                <SortHeader label="Last contacted" sortKey="last_contacted_at" currentKey={sortKey} currentDir={sortDir} onChange={toggleSort} />
              </th>
              <th className="py-2.5 px-3 text-left hidden lg:table-cell">
                <SortHeader label="Updated" sortKey="updated_at" currentKey={sortKey} currentDir={sortDir} onChange={toggleSort} />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-10 text-center text-slate-400 dark:text-slate-500 text-sm">
                  No leads match your filters
                </td>
              </tr>
            ) : filtered.map(lead => {
              const PrIcon = PRIORITY_ICON[lead.priority] || Thermometer;
              const isSel = selectedIds.has(lead.id);
              const tel = lead.phone ? `tel:${lead.phone.replace(/\s+/g, '')}` : null;
              const sms = lead.phone ? `sms:${lead.phone.replace(/\s+/g, '')}` : null;
              const wa = waHref(lead.phone);
              const mail = lead.email ? `mailto:${lead.email}` : null;
              return (
                <tr
                  key={lead.id}
                  className={cn(
                    'hover:bg-slate-50 dark:hover:bg-slate-800/60 transition',
                    isSel && 'bg-emerald-50 dark:bg-emerald-950/30'
                  )}
                  data-testid={`table-row-${lead.id}`}
                >
                  <td className="py-2.5 px-3">
                    <button onClick={() => toggleSelect(lead.id)} aria-label="Select row" data-testid={`table-select-${lead.id}`}>
                      {isSel ? <CheckSquare className="w-4 h-4 text-emerald-500" /> : <Square className="w-4 h-4 text-slate-400" />}
                    </button>
                  </td>
                  <td className="py-2.5 px-3">
                    <button
                      onClick={() => onSelectLead && onSelectLead(lead)}
                      className="font-medium text-slate-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 text-left"
                      data-testid={`table-name-${lead.id}`}
                    >
                      {lead.name}
                    </button>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{lead.lead_type}</div>
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex flex-wrap items-center gap-1">
                      {tel && (
                        <a href={tel} className="p-1.5 rounded bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-500/20" title="Call" onClick={e => e.stopPropagation()}>
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {sms && (
                        <a href={sms} className="p-1.5 rounded bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-500/20" title="SMS" onClick={e => e.stopPropagation()}>
                          <MessageSquare className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {wa && (
                        <a href={wa} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-500/20" title="WhatsApp" onClick={e => e.stopPropagation()}>
                          <MessageCircle className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {mail && (
                        <a href={mail} className="p-1.5 rounded bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-500/20" title="Email" onClick={e => e.stopPropagation()}>
                          <Mail className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 px-3">
                    <select
                      value={lead.status || 'new_lead'}
                      onChange={(e) => handleStatusChange(lead, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      className="text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-1.5 py-1 max-w-[150px]"
                      data-testid={`table-status-${lead.id}`}
                    >
                      {STATUS_ORDER.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="py-2.5 px-3">
                    <button
                      onClick={() => {
                        const next = lead.priority === 'Hot' ? 'Warm' : lead.priority === 'Warm' ? 'Cold' : 'Hot';
                        handlePriorityChange(lead, next);
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-medium hover:opacity-80"
                      title="Click to cycle priority"
                      data-testid={`table-priority-${lead.id}`}
                    >
                      <PrIcon className={cn('w-3.5 h-3.5', PRIORITY_COLOR[lead.priority])} />
                      <span className={PRIORITY_COLOR[lead.priority]}>{lead.priority}</span>
                    </button>
                  </td>
                  <td className="py-2.5 px-3 hidden md:table-cell">
                    <Badge variant="outline" className="text-xs dark:border-slate-700 dark:text-slate-300">{lead.lead_source}</Badge>
                  </td>
                  <td className="py-2.5 px-3 hidden lg:table-cell text-xs text-slate-500 dark:text-slate-400">{formatDate(lead.last_contacted_at)}</td>
                  <td className="py-2.5 px-3 hidden lg:table-cell text-xs text-slate-500 dark:text-slate-400">{formatDate(lead.updated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400 px-1">
        Showing {filtered.length} of {leads.length} leads. {STATUS_LABEL[statusFilter] && statusFilter !== 'all' && `Filter: ${STATUS_LABEL[statusFilter]}`}
      </p>
    </div>
  );
}
